import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import Icon from '@/components/Icon/Icon';
import { type PlaceOption, PlaceSelect } from '@/components/Select/PlaceSelect';
import { LabelPlace } from '@/features/place/components/LabelPlace/LabelPlace';
import { apiClient } from '@/lib/api/client';
import { getPath } from '@/lib/api/path';

import {
  CFormItemBase,
  type CFormItemProps,
  buildCFormItem,
} from '../CFormItem';

type Props = CFormItemProps & {
  value?: string;
  allowEmpty?: boolean;
  readComponent?: ReactNode;
  onChange: (placeId?: string) => void;
  inputWidth?: number | string;
  inputClassName?: string;
  showPlaceLink?: boolean;
};

/**
 * Card-based Form / PlaceItem
 */
export const CFormPlaceSelectItem = buildCFormItem<Props>((props: Props) => {
  const { data: selectedPlace } = useQuery({
    queryKey: [getPath('/v2/components/place-select'), props.value] as const,
    queryFn: async () => {
      if (!props.value) return null;

      const res = await apiClient.GET('/v2/components/place-select', {
        params: {
          query: {
            idIn: [props.value],
            pageSize: 1,
            getAddress: false,
            getPhone: false,
            getEmail: false,
          },
        },
      });

      return res.data?.items[0] ?? null;
    },
    enabled: !!props.value,
  });

  const handlePlaceChange = (place?: PlaceOption) => {
    props.onChange(place?.id);
  };

  return (
    <CFormItemBase
      {...props}
      readComponent={
        props.readComponent ??
        (props.value && selectedPlace ? (
          <div className="flex items-center gap-2">
            {props.showPlaceLink && selectedPlace.id && (
              <Link
                to="/places/$id"
                target="_blank"
                rel="noopener noreferrer"
                params={{ id: selectedPlace.id }}
              >
                <Icon iconName="SquareArrowOutUpRightIcon" />
              </Link>
            )}
            <LabelPlace
              name={selectedPlace.name}
              avatarUrl={selectedPlace.avatarUrl}
            />
          </div>
        ) : (
          <></>
        ))
      }
      writeComponent={
        <PlaceSelect
          disabled={props.readonly}
          allowEmpty={props.allowEmpty}
          value={props.value}
          onChange={handlePlaceChange}
          width={
            typeof props.inputWidth === 'number' ? props.inputWidth : undefined
          }
          className={props.inputClassName}
        />
      }
    />
  );
});
